/* eslint eslint-comments/no-use: off */
// eslint-disable-next-line @github-ui/github-monorepo/filename-convention
import {VALUES} from '@github-ui/item-picker-base/constants/values'
import {
  getIssueSearchQueries,
  getSemanticallySimilarQuery,
  getSemanticSearchType,
} from '@github-ui/item-picker-base/shared'
import {useQuery} from '@github-ui/react-query'
import {createServerPersistedQueryPersister} from '@github-ui/react-query-persister'
import {DEFAULT_RELAY_CACHE_TTL} from '@github-ui/relay-query-cache/helpers'
import {IssuePickerFilteringServerQuery} from '@github-ui/server-persisted-queries'
import {useAnalytics} from '@github-ui/use-analytics'
import {useDebounce} from '@github-ui/use-debounce'
import {useEffect, useMemo, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'

import type {IssuePickerItem} from '../components/IssuePicker'
import {fetchIssueFiltering, issueFilteringQueryKey} from './fetch-issue-filtering'

// Handles debouncing queries to fetch issues based on the current filter
export const useIssueFiltering = (
  filter: string,
  owner: string = '',
  repositoryNameWithOwner: string,
  hiddenIssueIds?: string[],
  withParent: boolean = false,
  requireTargetTriage: boolean = false,
  // Issue title used to surface semantically similar suggestions when the filter is empty.
  semanticQuery?: string,
): {
  isLoading: boolean
  isError: boolean
  items: Map<string, IssuePickerItem>
  // Ids of the issues that came from the semantic bucket, so callers can attribute a selection to
  // semantic vs lexical search for engagement telemetry.
  semanticIssueIds: Set<string>
} => {
  const environment = useRelayEnvironment()
  const [debouncedFilter, setDebouncedFilter] = useState(filter)
  const semanticSearchType = getSemanticSearchType(environment)
  const debounceFilter = useDebounce((nextFilter: string) => setDebouncedFilter(nextFilter), VALUES.pickerDebounceTime)
  useEffect(() => {
    debounceFilter(filter)
  }, [debounceFilter, filter])

  const semanticallySimilarQuery = semanticSearchType
    ? getSemanticallySimilarQuery(owner, semanticQuery, repositoryNameWithOwner)
    : ''
  const includeSemanticallySimilar = Boolean(semanticallySimilarQuery) && debouncedFilter.trim() === ''
  // Every search the user types produces its own query key, and none of those are worth keeping —
  // they'd fill up storage and never be read again. Only the unfiltered list, which is what the
  // picker shows the moment it opens, is cached.
  const isUnfiltered = debouncedFilter.trim() === '' && !semanticallySimilarQuery
  const {sendAnalyticsEvent} = useAnalytics({optional: true})
  const persister = useMemo(
    () =>
      createServerPersistedQueryPersister({
        queryHash: IssuePickerFilteringServerQuery,
        sendAnalyticsEvent,
        maxAgeMs: DEFAULT_RELAY_CACHE_TTL,
        // Restored issues may be stale within their TTL, so revalidate after restoring them.
        alwaysRefetch: true,
      }),
    [sendAnalyticsEvent],
  )
  const variables = {
    ...getIssueSearchQueries(owner, debouncedFilter, repositoryNameWithOwner),
    withParent,
    requireTargetTriage,
    semanticallySimilarQuery,
    includeSemanticallySimilar,
    ...(includeSemanticallySimilar && semanticSearchType ? {semanticSearchType} : {}),
  }
  const query = useQuery({
    queryKey: issueFilteringQueryKey(repositoryNameWithOwner, variables),
    queryFn: ({signal}) => fetchIssueFiltering(repositoryNameWithOwner, variables, signal),
    placeholderData: previousData => previousData,
    gcTime: isUnfiltered ? DEFAULT_RELAY_CACHE_TTL : undefined,
    persister: isUnfiltered ? persister : undefined,
  })
  const data = query.data ?? null

  const items: Map<string, IssuePickerItem> = useMemo(() => {
    if (!data) {
      return new Map()
    } else {
      // When resource is returned, we only want to show that issue as the user's intent is to
      // select a specific issue otherwise, we show all the issues from the search queries
      if (data.resource?.__typename === 'Issue') {
        // Best-effort hide of a directly-resolved issue the viewer can't triage (viewerCanUpdateMetadata
        // reflects base repo role); the mutation-level FORBIDDEN handling is the authoritative backstop.
        if (requireTargetTriage && data.resource.viewerCanUpdateMetadata !== true) {
          return new Map()
        }
        return new Map([[data.resource?.id, data.resource]])
      }

      // Order the unique issues from each of the queries. Changing the order of this spread
      // will change the relative priority of each individual search query. Semantically similar
      // results are placed first so they surface at the top.
      const searchResultNodes = [
        ...(data.semanticallySimilar?.nodes || []),
        ...(data.commenters?.nodes || []),
        ...(data.mentions?.nodes || []),
        ...(data.assignee?.nodes || []),
        ...(data.author?.nodes || []),
        ...(data.other?.nodes || []),
      ]

      // viewerCanUpdateMetadata is only @include'd when requireTargetTriage is true, so guard the read.
      const issueNodes = searchResultNodes.filter(
        (node): node is IssuePickerItem =>
          node?.__typename === 'Issue' && (!requireTargetTriage || node.viewerCanUpdateMetadata === true),
      )

      const issuesMap = new Map<string, IssuePickerItem>()
      for (const issue of issueNodes) {
        if (hiddenIssueIds?.some(id => id === issue.id)) {
          continue
        }

        issuesMap.set(issue.id, issue)
      }

      return issuesMap
    }
  }, [hiddenIssueIds, data, requireTargetTriage])

  const semanticIssueIds = useMemo(() => {
    const ids = new Set<string>()
    for (const node of data?.semanticallySimilar?.nodes || []) {
      if (node?.__typename === 'Issue') ids.add(node.id)
    }
    return ids
  }, [data])

  return {
    // isPending, not isFetching: restored issues are on hand immediately, and alwaysRefetch means a
    // revalidation is always running behind them. Reporting that as loading would hide the very data
    // the cache just handed us.
    isLoading: query.isPending,
    isError: query.isError,
    items,
    semanticIssueIds,
  }
}
